/**
 * PlaygroundDashboard.tsx
 * Main hub for all playground sections
 * 
 * Uses our shared UI components to showcase them in action
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Brand } from '@enfomentis/shared/config';
import { Palette, Layers, BarChart3, ChevronRight } from 'lucide-react';
import { PlaygroundHeader } from './shared';
import { Card, CardHeader, CardBody, CardFooter } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import type { PlaygroundSection } from './shared/types';


const playgroundSections: PlaygroundSection[] = [
  {
    id: 'components',
    title: 'UI Components',
    description: 'Interactive showcase of HeroUI components with Tailwind v4 styling',
    path: '/playground/components',
    status: 'available'
  },
  {
    id: 'themes',
    title: 'Theme System',
    description: 'Tailwind v4 theme system with CSS custom properties and dark mode',
    path: '/playground/themes',
    status: 'available'
  },
  {
    id: 'data',
    title: 'Data Visualization',
    description: 'AG Grid tables and charts with theme integration',
    path: '/playground/data',
    status: 'available'
  }
];

const getSectionIcon = (id: string) => {
  switch (id) {
    case 'components': return Layers;
    case 'themes': return Palette;
    case 'data': return BarChart3;
    default: return Layers;
  }
};

export const PlaygroundDashboard: React.FC = () => {
  const navigate = useNavigate();

  const handleSectionClick = (path: string) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <PlaygroundHeader
        title={`${Brand.displayName} Component Playground`}
        description={`Interactive showcase of ${Brand.displayName}'s design system`}
      />

      <div className="mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {playgroundSections.map((section) => {
            const Icon = getSectionIcon(section.id);
            
            return (
              <Card
                key={section.id}
                shadow="lg" isHoverable
                isPressable
                onPress={() => handleSectionClick(section.path)}
                className="group transition-all duration-200"
              >
                <CardHeader>
                  <div className="flex items-start justify-between w-full">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-text-primary group-hover:text-primary transition-colors">
                        {section.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <Chip color="success" size="sm" variant="flat">
                        {section.status === 'available' ? 'Ready' : section.status}
                      </Chip>
                      <ChevronRight className="w-4 h-4 text-text-tertiary group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                </CardHeader>
                
                <CardBody>
                  <p className="text-text-secondary leading-relaxed">
                    {section.description}
                  </p>
                </CardBody>
                
                <CardFooter>
                  <Button
                    color="primary"
                    size="sm"
                    onPress={() => handleSectionClick(section.path)}
                    className="w-full group-hover:shadow-md transition-shadow"
                  >
                    Explore {section.title}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="mt-12">
          <Card>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary mb-1">8+</div>
                  <div className="text-sm text-text-secondary">HeroUI Components</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary mb-1">50+</div>
                  <div className="text-sm text-text-secondary">Theme Colors</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary mb-1">100%</div>
                  <div className="text-sm text-text-secondary">Accessibility</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary mb-1">∞</div>
                  <div className="text-sm text-text-secondary">Customizable</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};